import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Home, User, DollarSign, Calendar, Check } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const TABS = [
  { id: 'apartment', label: 'Lägenhet', icon: Home },
  { id: 'tenant', label: 'Hyresgäst', icon: User },
  { id: 'economy', label: 'Ekonomi', icon: DollarSign },
  { id: 'period', label: 'Period', icon: Calendar },
];

export default function EditApplicationModal({ open, onClose, application, onSuccess }) {
  const [activeTab, setActiveTab] = useState('apartment');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    // Lägenhet
    address: '',
    apartmentNumber: '',
    city: '',
    zipCode: '',
    rooms: '',
    squareMeter: '',
    reason: '',

    // Hyresgäst
    tenantFirstName: '',
    tenantLastName: '',
    tenantEmail: '',
    tenantPhoneNumber: '',
    tenantPersonalNumber: '',

    // Ekonomi
    rentalFee: '',
    deposit: '',
    clearingnummer: '',
    bankAccountNumber: '',

    // Period
    fromDate: '',
    toDate: '',
    contractStartDate: '',
    contractEndDate: '',

    // Tjänster
    heatingIncluded: false,
    hotWaterIncluded: false,
    electricityIncluded: false,
    internetIncluded: false,
    parkingIncluded: false,
    furnitureIncluded: false,

    // Övrigt
    miscellaneousNotes: '',
  });

  // Load application data when modal opens
  useEffect(() => {
    if (application && open) {
      setFormData({
        address: application.address || '',
        apartmentNumber: application.apartmentNumber || '',
        city: application.city || '',
        zipCode: application.zipCode || '',
        rooms: application.rooms || '',
        squareMeter: application.squareMeter || '',
        reason: application.reason || '',

        tenantFirstName: application.tenantFirstName || '',
        tenantLastName: application.tenantLastName || '',
        tenantEmail: application.tenantEmail || '',
        tenantPhoneNumber: application.tenantPhone || application.tenantPhoneNumber || '',
        tenantPersonalNumber: application.tenantPersonalNumber || '',

        rentalFee: application.rent || application.rentalFee || '',
        deposit: application.deposit || '',
        clearingnummer: application.clearing || application.clearingnummer || '',
        bankAccountNumber: application.bankAccount || application.bankAccountNumber || '',

        fromDate: application.fromDate || '',
        toDate: application.toDate || '',
        contractStartDate: application.contractStartDate || '',
        contractEndDate: application.contractEndDate || '',

        heatingIncluded: application.includeHeating || application.heatingIncluded || false,
        hotWaterIncluded: application.includeWater || application.hotWaterIncluded || false,
        electricityIncluded: application.includeElectricity || application.electricityIncluded || false,
        internetIncluded: application.includeInternet || application.internetIncluded || false,
        parkingIncluded: application.includeParking || application.parkingIncluded || false,
        furnitureIncluded: application.includeFurniture || application.furnitureIncluded || false,

        miscellaneousNotes: application.miscellaneousNotes || '',
      });
      setActiveTab('apartment');
      setError('');
    }
  }, [application, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!application?.id) {
      setError('Ingen ansökan vald');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const rentNumber = formData.rentalFee ? Number(formData.rentalFee) : 0;
      const depositNumber = formData.deposit ? Number(formData.deposit) : 0;

      // Map form fields to database fields.
      // Vi skriver till BÅDE de gamla och de nya fältnamnen så att alla
      // läs-vyer (ApplicationDetailModal m.fl.) ser uppdaterade värden.
      // En tidigare commit införde "konsekventa" namn (tenantPhone/rent/
      // clearing/bankAccount) men resten av kodbasen läser fortfarande
      // från de gamla (tenantPhoneNumber/rentalFee/clearingnummer/
      // bankAccountNumber) — vilket gjorde att uppdateringar var osynliga.
      const updateData = {
        // Lägenhet
        address: formData.address,
        apartmentNumber: formData.apartmentNumber,
        city: formData.city,
        zipCode: formData.zipCode,
        rooms: formData.rooms,
        squareMeter: formData.squareMeter,
        reason: formData.reason,

        // Hyresgäst (skriv till båda fältnamnen)
        tenantFirstName: formData.tenantFirstName,
        tenantLastName: formData.tenantLastName,
        tenantEmail: formData.tenantEmail,
        tenantPhoneNumber: formData.tenantPhoneNumber,
        tenantPhone: formData.tenantPhoneNumber,
        tenantPersonalNumber: formData.tenantPersonalNumber,

        // Ekonomi (skriv till båda fältnamnen)
        rentalFee: rentNumber,
        rent: rentNumber,
        deposit: depositNumber,
        clearingnummer: formData.clearingnummer,
        clearing: formData.clearingnummer,
        bankAccountNumber: formData.bankAccountNumber,
        bankAccount: formData.bankAccountNumber,

        // Period
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,

        // Tjänster (skriv till båda fältnamnen)
        heatingIncluded: formData.heatingIncluded,
        includeHeating: formData.heatingIncluded,
        hotWaterIncluded: formData.hotWaterIncluded,
        includeWater: formData.hotWaterIncluded,
        electricityIncluded: formData.electricityIncluded,
        includeElectricity: formData.electricityIncluded,
        internetIncluded: formData.internetIncluded,
        includeInternet: formData.internetIncluded,
        parkingIncluded: formData.parkingIncluded,
        includeParking: formData.parkingIncluded,
        furnitureIncluded: formData.furnitureIncluded,
        includeFurniture: formData.furnitureIncluded,

        // Övrigt
        miscellaneousNotes: formData.miscellaneousNotes,

        // Metadata
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'unknown',
      };

      // Update Firestore
      await updateDoc(doc(db, 'applications', application.id), updateData);

      // Success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EditApplicationModal] Update failed:', err);
      setError(err?.message
        ? `Kunde inte uppdatera ansökan: ${err.message}`
        : 'Kunde inte uppdatera ansökan. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={onClose} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-4xl mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#E2EAF0]">
          <button
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500"
            onClick={onClose}
            disabled={saving}
            aria-label="Stäng"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
            Redigera uthyrning
          </h2>
          <p className="text-sm text-[#626262] mt-1">
            {application.address || 'Adress ej angiven'}, Lgh {application.apartmentNumber || 'N/A'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#E2EAF0] px-6 flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#62B8E9]'
                    : 'text-[#626262] hover:text-[#222222]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#62B8E9]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Apartment Tab */}
          {activeTab === 'apartment' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#222222] font-['Poppins'] mb-4">Lägenhetsinformation</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Adress *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. Storgatan 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Lägenhetsnummer *</label>
                  <input
                    type="text"
                    value={formData.apartmentNumber}
                    onChange={(e) => handleChange('apartmentNumber', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 1001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Stad *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. Stockholm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Postnummer</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 123 45"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Antal rum</label>
                  <input
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => handleChange('rooms', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Storlek (kvm)</label>
                  <input
                    type="number"
                    value={formData.squareMeter}
                    onChange={(e) => handleChange('squareMeter', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 65"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">Uthyrningsanledning</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => handleChange('reason', e.target.value)}
                  className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
                  rows={3}
                  placeholder="T.ex. Tillfälligt arbete eller studier på annan ort"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">Inkluderade tjänster</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'heatingIncluded', label: 'Uppvärmning' },
                    { key: 'hotWaterIncluded', label: 'Varmvatten' },
                    { key: 'electricityIncluded', label: 'El' },
                    { key: 'internetIncluded', label: 'Internet' },
                    { key: 'parkingIncluded', label: 'Parkering' },
                    { key: 'furnitureIncluded', label: 'Möblerad' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[key]}
                        onChange={(e) => handleChange(key, e.target.checked)}
                        className="w-4 h-4 text-[#62B8E9] border-[#E2EAF0] rounded focus:ring-2 focus:ring-[#62B8E9]"
                      />
                      <span className="text-sm text-[#222222]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tenant Tab */}
          {activeTab === 'tenant' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#222222] font-['Poppins'] mb-4">Hyresgästinformation</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Förnamn</label>
                  <input
                    type="text"
                    value={formData.tenantFirstName}
                    onChange={(e) => handleChange('tenantFirstName', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. Anna"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Efternamn</label>
                  <input
                    type="text"
                    value={formData.tenantLastName}
                    onChange={(e) => handleChange('tenantLastName', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. Andersson"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">E-post</label>
                  <input
                    type="email"
                    value={formData.tenantEmail}
                    onChange={(e) => handleChange('tenantEmail', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="anna.andersson@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Telefonnummer</label>
                  <input
                    type="tel"
                    value={formData.tenantPhoneNumber}
                    onChange={(e) => handleChange('tenantPhoneNumber', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="070-123 45 67"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#222222] mb-2">Personnummer</label>
                  <input
                    type="text"
                    value={formData.tenantPersonalNumber}
                    onChange={(e) => handleChange('tenantPersonalNumber', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="ÅÅÅÅMMDD-XXXX"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Economy Tab */}
          {activeTab === 'economy' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#222222] font-['Poppins'] mb-4">Ekonomisk information</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Hyresavgift (kr/mån)</label>
                  <input
                    type="number"
                    value={formData.rentalFee}
                    onChange={(e) => handleChange('rentalFee', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 10000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Deposition (kr)</label>
                  <input
                    type="number"
                    value={formData.deposit}
                    onChange={(e) => handleChange('deposit', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 20000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Clearingnummer</label>
                  <input
                    type="text"
                    value={formData.clearingnummer}
                    onChange={(e) => handleChange('clearingnummer', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 1234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-2">Kontonummer</label>
                  <input
                    type="text"
                    value={formData.bankAccountNumber}
                    onChange={(e) => handleChange('bankAccountNumber', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="T.ex. 123456789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">Övriga noteringar</label>
                <textarea
                  value={formData.miscellaneousNotes}
                  onChange={(e) => handleChange('miscellaneousNotes', e.target.value)}
                  className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
                  rows={4}
                  placeholder="Övrig information om uthyrningen..."
                />
              </div>
            </div>
          )}

          {/* Period Tab */}
          {activeTab === 'period' && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-[#222222] font-['Poppins'] mb-4">Uthyrningsperiod</h3>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-[#222222] mb-3">Godkänd uthyrningsperiod</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#222222] mb-2">Från datum *</label>
                      <input
                        type="date"
                        value={formData.fromDate}
                        onChange={(e) => handleChange('fromDate', e.target.value)}
                        className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#222222] mb-2">Till datum *</label>
                      <input
                        type="date"
                        value={formData.toDate}
                        onChange={(e) => handleChange('toDate', e.target.value)}
                        className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-[#222222] mb-3">Avtalsperiod (inom uthyrningsperioden)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#222222] mb-2">Avtalets startdatum</label>
                      <input
                        type="date"
                        value={formData.contractStartDate}
                        onChange={(e) => handleChange('contractStartDate', e.target.value)}
                        className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#222222] mb-2">Avtalets slutdatum</label>
                      <input
                        type="date"
                        value={formData.contractEndDate}
                        onChange={(e) => handleChange('contractEndDate', e.target.value)}
                        className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="px-6 pb-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-[#E2EAF0] flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border border-[#E2EAF0] text-[#626262] rounded-lg font-semibold hover:bg-[#F9FCFF] transition-colors disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-[#62B8E9] text-white rounded-lg font-semibold hover:bg-[#4A9FD1] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              'Sparar...'
            ) : (
              <>
                <Check className="w-4 h-4" />
                Spara ändringar
              </>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
